import { InputFieldProps } from "../types";
import { isEmpty } from "../../../../../../../helpers/object/arePlainObjectsEqual";

const filterToImportant = (
  importantKeys: string[],
  data: { key: string; value: string | number }[],
) =>
  data.reduce(
    (acc, { key, value }) =>
      importantKeys.includes(key) &&
      (typeof value === "number" || !!value.length)
        ? { ...acc, [key]: value }
        : acc,
    {} as { [key: string]: string | number },
  );

// For a select-type field, the raw stored value (e.g. a campaign id) isn't what the
// user sees — resolve it to the matching option's displayed label instead.
const resolveDisplayValue = (
  row: InputFieldProps[],
  key: string,
  value: string | number,
): string | number => {
  const field = row.find((item) => item.key === key);
  if (!field?.options) return value;
  const matched = field.options.find((option) =>
    typeof option === "string" ? option === value : option.value === value,
  );
  if (!matched) return value;
  return typeof matched === "string" ? matched : matched.title;
};

export type DuplicateDataWarning = { index: number; message: string };

// Returns at most one combined warning per row index, for rows where an important field's value
// is repeated in another row (every row of a duplicate set is flagged) or is one of builtInValues.
// Only importantKeys are compared, empty strings are ignored, and matching is exact (case-sensitive).
export const duplicateDataMessages = (
  fieldRender: InputFieldProps[][],
  importantKeys: string[],
  builtInValues: string[] = [],
): DuplicateDataWarning[] => {
  const importantByRow = fieldRender.map((row) =>
    filterToImportant(importantKeys, row),
  );

  const toDisplayValues = (
    entries: [string, string | number][],
    index: number,
  ) =>
    entries.map(([key, value]) =>
      resolveDisplayValue(fieldRender[index], key, value),
    );

  return importantByRow.reduce<DuplicateDataWarning[]>(
    (warnings, rowValues, index) => {
      if (Object.keys(rowValues).length < 1) return warnings;

      const duplicateData = Object.entries(rowValues).filter(([key, value]) =>
        importantByRow.some(
          (otherRowValues, otherIndex) =>
            otherIndex !== index && otherRowValues[key] === value,
        ),
      );
      const builtInData = Object.entries(rowValues).filter(([, value]) =>
        builtInValues.includes(String(value)),
      );
      if (duplicateData.length === 0 && builtInData.length === 0)
        return warnings;

      const messages: string[] = [];

      if (duplicateData.length > 0) {
        const displayValues = toDisplayValues(duplicateData, index);
        messages.push(
          importantKeys.length === 1
            ? `Entry ${displayValues[0]} is duplicated`
            : `${displayValues.length > 1 ? "Entries" : "Entry"} ${duplicateData.map(([key], i) => `${key}:${displayValues[i]}`).join(", ")} ${displayValues.length > 1 ? "are" : "is"} duplicated`,
        );
      }

      if (builtInData.length > 0) {
        const displayValues = toDisplayValues(builtInData, index);
        messages.push(
          `${displayValues.length > 1 ? "Entries" : "Entry"} ${displayValues.join(", ")} is a built-in standard`,
        );
      }

      return [...warnings, { index, message: messages.join("; ") }];
    },
    [],
  );
};

// For several keys: "query and url must be provided together" / "a, b and c must be …"
export const incompleteDataMessage = (keys: string[]) =>
  `${keys.slice(0, -1).join(", ")} and ${keys[keys.length - 1]} must be provided together`;

// Indexes of rows where some important fields are filled and others are empty. Fully blank rows
// aren't included: they're dropped on save (getFieldsProps' filterOutEmptyFields, same isEmpty rule).
export const incompleteRowIndexes = (
  fieldRender: InputFieldProps[][],
  importantKeys: string[],
): number[] =>
  fieldRender.flatMap((row, index) => {
    const important = row.filter(({ key }) => importantKeys.includes(key));
    const filledCount = important.filter(({ value }) => !isEmpty(value)).length;
    return filledCount > 0 && filledCount < important.length ? [index] : [];
  });

// "name pattern: a-z0-9_" — the pattern without regex-only symbols.
export const patternMessage = (key: string, pattern: string) =>
  `${key} pattern: ${pattern.replace(/[[\]^$+*?(){}|\\]/g, "")}`;
